import { Typography, LinkButton } from 'components/Contentful'
import { BLOCKS } from '@contentful/rich-text-types'
import { ExternalLink } from 'react-feather'
import { Image } from 'components/Image'
import { formatDollar } from 'pageSections/DebtPayoffCalculator/utils'

const BADGE_STYLES = {
  loan: 'bg-[#EFF5FF] text-(--blue-300,#3B6AF7)',
  nonLoan: 'bg-[#EFF5FF] text-(--blue-300,#3B6AF7)',
  creditors: 'bg-[#FFF4F0] text-(--content-negative,#CF264E)',
}

const COMPACT_BUTTON_CLASSES =
  'whitespace-nowrap [&_p]:whitespace-nowrap [&_p]:text-[14px] min-[768px]:[&_p]:text-[18px] max-[767px]:h-8! max-[767px]:min-h-8! max-[767px]:max-w-[145px] max-[767px]:[border-radius:8px]! max-[767px]:[&_*]:[border-radius:8px]! max-[767px]:px-0! max-[767px]:py-0! max-[767px]:[&_.base-styles]:min-h-8! max-[767px]:[&_.base-styles]:[border-radius:8px]! max-[767px]:[&_.base-styles]:py-0!'

const Metric = ({ value, label }) => (
  <div>
    <Typography
      component="span"
      className="flex text-[20px]! min-[768px]:text-[24px]! leading-[120%]! font-bold text-[#1D252F]"
      content={value}
    />
    <Typography
      className="mt-0.5! flex text-[12px]! min-[768px]:text-[16px]! leading-[120%]! font-normal text-[#42546B]"
      content={label}
    />
  </div>
)

export const ComparisonCard = ({
  iconSrc,
  iconAlt,
  title,
  subheading,
  badgeText,
  badgeVariant = 'loan',
  active = true,
  hasData = active,
  resultMonthly = 0,
  resultMonthlyText,
  resultTotal = 0,
  resultTotalText,
  resultMonths = 0,
  resultMonthsText,
  monthlySuffix = '',
  monthsSuffix = '',
  unavailableBadgeText = 'Unavailable',
  learnMore,
  cta,
  sectionVariationObject,
  externalLink = false,
  sectionTitle,
  pos,
}) => {
  const externalIcon = externalLink ? (
    <ExternalLink
      size={18}
      color="#FFFFFF"
      aria-hidden="true"
      className="size-[18px]! min-h-[18px]! min-w-[18px]! shrink-0"
    />
  ) : null

  return (
    <div
      className="relative z-10 flex h-full flex-col rounded-[10px] border border-solid border-[#C0CBD8] bg-white p-4 min-[768px]:px-[18px] min-[768px]:py-[23px] max-[767px]:max-h-[399px] max-[767px]:min-w-[181px] min-[768px]:min-w-[225px] lg:max-h-[456px] lg:px-[18px] lg:py-[23px]"
      data-testid="DebtComparisonCalculator-card"
    >
      <div className="mb-4 flex flex-wrap items-center gap-2 breakpointMd:justify-between">
        <Image
          alt={iconAlt}
          height="50"
          width="50"
          src={iconSrc}
          className="h-[30px] w-[30px] shrink-0 breakpointMd:h-12.5 breakpointMd:w-12.5"
        />
        <span
          className={`inline-flex h-8 items-center text-nowrap rounded-full px-3 font-ultramarine not-italic font-bold breakpointMd:h-auto breakpointMd:py-1 ${
            hasData
              ? `text-[14px]! breakpointMd:text-[16px]! leading-[120%]! breakpointMd:leading-[140%]! tracking-[0.28px] breakpointMd:tracking-normal! ${BADGE_STYLES[badgeVariant]}`
              : 'bg-(--background-secondary,#F8F9FC) text-(--content-informational,#42546B) text-[14px]! min-[768px]:text-[16px]! leading-[140%]!'
          }`}
          data-testid="DebtComparisonCalculator-card-badge"
        >
          {hasData ? badgeText : unavailableBadgeText}
        </span>
      </div>

      <Typography
        component="h3"
        className={`${
          subheading ? 'mb-0! min-[768px]:mb-2! shrink-0' : 'mb-4 shrink-0'
        } font-ultramarine text-[18px]! min-[768px]:text-[24px]! not-italic font-bold leading-[130%]! tracking-normal! text-(--content-primary,#1D252F)!`}
        content={title}
        data-testid="DebtComparisonCalculator-card-title"
      />

      {subheading && (
        <p className="mt-0! pt-0! min-[768px]:pt-2 mb-4 min-[768px]:mb-3 lg:mb-0 min-h-[2lh] lg:min-h-0 shrink-0 font-ultramarine text-[14px] font-normal leading-[140%] text-(--content-primary,#1D252F)">
          {subheading}
        </p>
      )}

      <div
        className={`mb-3 breakpointMd:mb-4 h-px shrink-0 bg-(--border-opaque,#E7ECF3) ${
          subheading ? 'lg:mt-4' : ''
        }`}
        aria-hidden="true"
      />

      <div
        className="flex min-h-0 flex-1 flex-col justify-between gap-2"
        data-testid="DebtComparisonCalculator-card-info"
      >
        <Metric
          value={hasData ? `$${formatDollar(resultMonthly)}${monthlySuffix}` : '$ -'}
          label={resultMonthlyText}
        />
        <Metric value={hasData ? `$${formatDollar(resultTotal)}` : '$ -'} label={resultTotalText} />
        <Metric
          value={hasData ? `${resultMonths} ${monthsSuffix}` : `- ${monthsSuffix || 'months'}`}
          label={resultMonthsText}
        />
      </div>
      {!hasData && learnMore?.link && learnMore?.text && (
        <div className="relative mt-auto shrink-0 pt-2 min-[768px]:top-[7px]">
          <LinkButton
            className={COMPACT_BUTTON_CLASSES}
            track={{
              click_position: pos >= 0 ? pos + 1 : '0',
              click_id: learnMore.text,
              click_text: learnMore.text,
              nav_link_section: sectionTitle,
              track_event: 'internal_campaign_click',
              event_action: 'button_click',
              event_type: 'button_click',
              sectionVariationObject,
            }}
            content={{ linkText: learnMore.text, linkHref: learnMore.link }}
            endIcon={externalIcon}
            typographicOptions={{
              variantOverride: { [BLOCKS.PARAGRAPH]: 'bodyLg' },
              fontWeight: 'bold',
            }}
            color="secondary"
            variant={LinkButton.VARIANTS.CONTAINED}
            fullWidth={true}
          />
        </div>
      )}
      {hasData && active && cta?.link && cta?.text && (
        <div className="relative mt-auto shrink-0 pt-2 min-[768px]:top-[7px]">
          <LinkButton
            className={COMPACT_BUTTON_CLASSES}
            track={{
              click_position: pos >= 0 ? pos + 1 : '0',
              click_id: cta.text,
              click_text: cta.text,
              nav_link_section: sectionTitle,
              track_event: 'internal_campaign_click',
              event_action: 'button_click',
              event_type: 'button_click',
              ...cta.track,
              sectionVariationObject,
            }}
            content={{ linkText: cta.text, linkHref: cta.link }}
            queryParamsToPreserve={cta.queryParamsToPreserve}
            endIcon={externalIcon}
            typographicOptions={{
              variantOverride: { [BLOCKS.PARAGRAPH]: 'bodyLg' },
              fontWeight: 'bold',
            }}
            color="primary"
            variant={LinkButton.VARIANTS.CONTAINED}
            fullWidth={true}
          />
        </div>
      )}
    </div>
  )
}
